import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    GamePhase, PieceType, Color, PromotionRule, GameVariation, Position,
    Piece, PlayerPieces, CastlingRights, GameMove, GameState, Board, OnlineGameSnapshot, OnlineRoomConfig, TimeControl
} from '../types';
import { cloneBoard } from '../utils/boardUtils';
import { formatTime, applyIncrement as applyIncrementUtil, canControlTurnBeforeTimerStart } from '../utils/timeControlUtils';
import { chess960PositionFromId } from '../utils/chess960Utils';
import {
    applyMove,
    buildInitialBoardFromBackRank,
    convertKingOnRookToCastling as convertKingOnRookToCastlingEngine,
    EnginePosition,
    generateMoveNotation as generateMoveNotationEngine,
    getBoardGameStatus,
    getCastlingSideFromMove as getCastlingSideFromMoveEngine,
    getCheckStatusAfterMove as getCheckStatusAfterMoveEngine,
    getLegalMoves,
    getPromotionPieceTypes,
    isInCheckOnBoard,
} from '../engine/chessEngine';

export const useChessGame = () => {
    const DEFAULT_GAME_VARIATION: GameVariation = 'chessx32';
    const DEFAULT_PLAYER_PIECES: PlayerPieces = { white: 'A', black: 'A' };
    const DEFAULT_PROMOTION_RULE: PromotionRule = 'full';

    const [gameVariation, setGameVariationState] = useState<GameVariation>(DEFAULT_GAME_VARIATION);
    const [gamePhase, setGamePhase] = useState<GamePhase>('setup');
    // Live view of the phase for long-lived closures (e.g. the room socket's
    // message handler) that must not capture a stale phase.
    const gamePhaseRef = useRef<GamePhase>(gamePhase);
    useEffect(() => {
        gamePhaseRef.current = gamePhase;
    }, [gamePhase]);
    const [playerPieces, setPlayerPieces] = useState<PlayerPieces>(DEFAULT_PLAYER_PIECES);
    const [promotionRule, setPromotionRule] = useState<PromotionRule>(DEFAULT_PROMOTION_RULE);
    const [board, setBoard] = useState<Board>(Array(8).fill(null).map(() => Array(8).fill(null)));
    const [currentPlayer, setCurrentPlayer] = useState<Color>('white');
    const [playerControlColor, setPlayerControlColor] = useState<Color | null>(null);
    const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
    const [validMoves, setValidMoves] = useState<Position[]>([]);
    const [gameHistory, setGameHistory] = useState<GameMove[]>([]);
    const [resignedBy, setResignedBy] = useState<Color | null>(null);
    const [drawOfferedBy, setDrawOfferedBy] = useState<Color | null>(null);
    const [drawAgreed, setDrawAgreed] = useState<boolean>(false);
    const [offerDrawOnNextMove, setOfferDrawOnNextMove] = useState<boolean>(false);
    const [promotionSquare, setPromotionSquare] = useState<Position | null>(null);
    const [draggedPiece, setDraggedPiece] = useState<Piece | null>(null);
    const [draggedFrom, setDraggedFrom] = useState<Position | null>(null);
    const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
    const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [enPassantTarget, setEnPassantTarget] = useState<Position | null>(null);
    const [castlingRights, setCastlingRights] = useState<CastlingRights>({
        whiteKingSide: true,
        whiteQueenSide: true,
        blackKingSide: true,
        blackQueenSide: true
    });
    const [kingPositions, setKingPositions] = useState<{ white: Position | null, black: Position | null }>({
        white: null,
        black: null
    });
    const [rookPositions, setRookPositions] = useState<{
        whiteKingSide: Position | null,
        whiteQueenSide: Position | null,
        blackKingSide: Position | null,
        blackQueenSide: Position | null
    }>({
        whiteKingSide: null,
        whiteQueenSide: null,
        blackKingSide: null,
        blackQueenSide: null
    });

    // Timer state
    const [timeControl, setTimeControl] = useState<TimeControl | null>({ minutes: 5, increment: 0, incrementType: 'none' });
    const [whiteTimeMs, setWhiteTimeMs] = useState<number>(0);
    const [blackTimeMs, setBlackTimeMs] = useState<number>(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_lastMoveTimestamp, setLastMoveTimestamp] = useState<number | null>(null);
    const [turnStartMs, setTurnStartMs] = useState<number | null>(null);
    const [timeoutDetected, setTimeoutDetected] = useState<boolean>(false);
    const [timeoutWinner, setTimeoutWinner] = useState<Color | null>(null);

    // Online snapshot sync bookkeeping. Reset whenever the hook resets into a
    // fresh game/room so a server-authored snapshot (e.g. the Blind Chess960
    // initial position, version 1) is never rejected against a stale counter.
    const lastSentSnapshotRef = useRef<string>('');
    const snapshotVersionRef = useRef<number>(0);
    const pendingResyncRef = useRef<boolean>(false);

    // The shared engine position: all rules logic in the shared engine reads
    // from this snapshot of the React state.
    const enginePosition: EnginePosition = useMemo(() => ({
        board,
        gameVariation,
        enPassantTarget,
        castlingRights,
        kingPositions,
        rookPositions
    }), [board, gameVariation, enPassantTarget, castlingRights, kingPositions, rookPositions]);

    // Generate standard chess starting position
    const generateStandardPosition = (): PieceType[] => {
        return ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'] as PieceType[];
    };

    // Helper function to get empty squares from array
    const getEmptySquares = (arr: (PieceType | null)[]): number[] => {
        return arr.map((piece, index) => piece === null ? index : null)
            .filter(i => i !== null) as number[];
    };

    // Helper function to select random square from array
    const selectRandomSquare = (squares: number[]): number => {
        return squares[Math.floor(Math.random() * squares.length)];
    };

    // Timer helper functions
    const applyIncrement = (_playerColor: Color, timeSpentMs: number): number => {
        if (!timeControl) return 0;
        return applyIncrementUtil(timeControl, timeSpentMs);
    };

    const getCurrentPlayerTimeRemaining = (): number => {
        if (!turnStartMs) return currentPlayer === 'white' ? whiteTimeMs : blackTimeMs;

        const now = Date.now();
        const elapsed = now - turnStartMs;
        const baseTime = currentPlayer === 'white' ? whiteTimeMs : blackTimeMs;

        return Math.max(0, baseTime - elapsed);
    };

    const forceTimerStart = (): void => {
        if (gamePhase !== 'playing') return;
        const gameStatus = getGameStatus();
        if (gameStatus.status !== 'ongoing') return;
        
        // Only allow forcing timer start if no moves have been played and timer hasn't started yet
        if (turnStartMs === null && gameHistory.length === 0) {
            setTurnStartMs(() => Date.now());
            setLastMoveTimestamp(() => Date.now());
        }
    };



    // Generate Chess960 starting position
    const generateChess960Position = (): PieceType[] => {
        const backRank: (PieceType | null)[] = Array(8).fill(null);

        // Place bishops on opposite colors
        const lightSquares = [1, 3, 5, 7];
        const darkSquares = [0, 2, 4, 6];
        const lightBishop = selectRandomSquare(lightSquares);
        const darkBishop = selectRandomSquare(darkSquares);

        backRank[lightBishop] = 'B';
        backRank[darkBishop] = 'B';

        // Place queen (will be replaced by chosen piece)
        const emptySquares = getEmptySquares(backRank);
        const queenPos = selectRandomSquare(emptySquares);
        backRank[queenPos] = 'Q';

        // Place knights
        const remainingSquares = getEmptySquares(backRank);
        const knight1Pos = selectRandomSquare(remainingSquares);
        backRank[knight1Pos] = 'N';

        const finalSquares = getEmptySquares(backRank);
        const knight2Pos = selectRandomSquare(finalSquares);
        backRank[knight2Pos] = 'N';

        // Place rooks and king (king between rooks)
        const lastThree = getEmptySquares(backRank);
        backRank[lastThree[0]] = 'R';
        backRank[lastThree[1]] = 'K';
        backRank[lastThree[2]] = 'R';

        return backRank as PieceType[];
    };

    // Initialize board with selected variation
    const initializeBoard = (variation?: GameVariation, piecesOverride?: PlayerPieces, chess960PositionId?: number | null): void => {
        const currentVariation = variation || gameVariation;
        const selectedPieces = piecesOverride ?? playerPieces;

        // Get back rank based on variation
        let backRank: PieceType[];
        if (currentVariation.includes('960')) {
            // For Chess960 variants, use specific position if provided, otherwise generate random
            if (chess960PositionId !== null && chess960PositionId !== undefined) {
                backRank = chess960PositionFromId(chess960PositionId);
            } else {
                backRank = generateChess960Position();
            }
        } else {
            // For non-Chess960 variants, always use standard position
            backRank = generateStandardPosition();
        }

        // Board construction, king/rook tracking, and castling rights are
        // shared with the server via the engine.
        const built = buildInitialBoardFromBackRank(currentVariation, backRank, selectedPieces);

        setKingPositions(built.kingPositions);
        setRookPositions(built.rookPositions);
        setCastlingRights(built.castlingRights);
        setBoard(built.board);
    };

    const startOnlineGame = (config: OnlineRoomConfig): void => {
        setGameVariationState(config.gameVariation);
        setPlayerPieces(config.playerPieces);
        setPromotionRule(config.promotionRule);
        setCurrentPlayer('white');
        setGameHistory([]);
        setPromotionSquare(null);
        setEnPassantTarget(null);
        setResignedBy(null);
        setDrawOfferedBy(null);
        setDrawAgreed(false);
        setOfferDrawOnNextMove(false);
        setSelectedSquare(null);
        setValidMoves([]);
        setDraggedPiece(null);
        setDraggedFrom(null);
        setTouchStartPos(null);
        setIsDragging(false);
        setGamePhase('playing');
        initializeBoard(config.gameVariation, config.playerPieces, config.chess960PositionId ?? null);

        // Initialize timers from config.timeControl when starting an online game
        const tc = config.timeControl ?? null;
        // Default to 5 minutes if no time control is specified
        const finalTimeControl = tc !== null ? tc : { minutes: 5, increment: 0, incrementType: 'none' as const };
        setTimeControl(finalTimeControl);
        
        if (finalTimeControl !== null) {
            const initialTimeMs = finalTimeControl.minutes * 60 * 1000;
            setWhiteTimeMs(initialTimeMs);
            setBlackTimeMs(initialTimeMs);
            setTurnStartMs(null);
        } else {
            setWhiteTimeMs(0);
            setBlackTimeMs(0);
            setTurnStartMs(null);
        }
        setLastMoveTimestamp(null);
        setTimeoutDetected(false);
        setTimeoutWinner(null);
    };

    /**
     * Blind Chess960: reset into the secret-setup phase. The game board is NOT
     * built here — the server authors the initial snapshot once both players
     * have submitted their back rank, and that snapshot drives the board.
     */
    const prepareBlindSetup = (config: OnlineRoomConfig): void => {
        setGameVariationState(config.gameVariation);
        setPlayerPieces(config.playerPieces);
        setPromotionRule(config.promotionRule);
        setGamePhase('setup');
        setBoard(Array(8).fill(null).map(() => Array(8).fill(null)));
        setCurrentPlayer('white');
        setGameHistory([]);
        setPromotionSquare(null);
        setEnPassantTarget(null);
        setResignedBy(null);
        setDrawOfferedBy(null);
        setDrawAgreed(false);
        setOfferDrawOnNextMove(false);
        setSelectedSquare(null);
        setValidMoves([]);
        setDraggedPiece(null);
        setDraggedFrom(null);
        setTouchStartPos(null);
        setIsDragging(false);
        setCastlingRights({
            whiteKingSide: true,
            whiteQueenSide: true,
            blackKingSide: true,
            blackQueenSide: true
        });
        setKingPositions({ white: null, black: null });
        setRookPositions({
            whiteKingSide: null,
            whiteQueenSide: null,
            blackKingSide: null,
            blackQueenSide: null
        });

        const tc = config.timeControl ?? { minutes: 5, increment: 0, incrementType: 'none' as const };
        setTimeControl(tc);
        setWhiteTimeMs(tc.minutes * 60 * 1000);
        setBlackTimeMs(tc.minutes * 60 * 1000);
        setTurnStartMs(null);
        setLastMoveTimestamp(null);
        setTimeoutDetected(false);
        setTimeoutWinner(null);

        // Reset sync bookkeeping so the server-authored initial snapshot
        // (which starts at version 1) is always accepted.
        lastSentSnapshotRef.current = '';
        snapshotVersionRef.current = 0;
        pendingResyncRef.current = false;
    };



    // ────────────────────────────────────────────────────────────────────────
    // Rules delegation: all chess rules logic lives in src/engine/chessEngine.ts
    // so the client hook and the Deno server share a single source of truth.
    // ────────────────────────────────────────────────────────────────────────

    const getCastlingSideFromMove = (
        piece: Piece,
        fromRow: number,
        fromCol: number,
        toRow: number,
        toCol: number
    ): 'kingside' | 'queenside' | null => getCastlingSideFromMoveEngine(enginePosition, piece, fromRow, fromCol, toRow, toCol);

    // Detect a king-onto-rook castling shortcut and convert it to the proper destination
    const convertKingOnRookToCastling = (fromRow: number, fromCol: number, toRow: number, toCol: number, piece: Piece): [number, number] =>
        convertKingOnRookToCastlingEngine(enginePosition, fromRow, fromCol, toRow, toCol, piece);

    // Get valid moves for a piece (with check validation)
    const getValidMoves = (row: number, col: number, piece: PieceType, color: Color): Position[] =>
        getLegalMoves(enginePosition, row, col, piece, color);

    // Check if the current player is in check
    const isInCheck = (color: Color): boolean => isInCheckOnBoard(board, color);

    // Execute a move including special moves like castling and en passant
    const executeMove = (fromRow: number, fromCol: number, toRow: number, toCol: number, piece: Piece): Board => {
        const result = applyMove(enginePosition, fromRow, fromCol, toRow, toCol, piece);
        setCastlingRights(result.castlingRights);
        setKingPositions(result.kingPositions);
        setRookPositions(result.rookPositions);
        setEnPassantTarget(result.enPassantTarget);
        return result.board;
    };

    // Get the current game status
    const getGameStatus = (): GameState => {
        if (timeoutWinner) {
            return { status: 'timeout', winner: timeoutWinner };
        }
        if (resignedBy) {
            return {
                status: 'resignation',
                winner: resignedBy === 'white' ? 'black' : 'white'
            };
        }
        if (drawAgreed) {
            return { status: 'draw_agreement' };
        }
        return getBoardGameStatus(enginePosition, currentPlayer);
    };

    const getCheckStatusAfterMove = (boardState: Board, nextPlayerToMove: Color): { isCheck: boolean; isCheckmate: boolean } =>
        getCheckStatusAfterMoveEngine({ ...enginePosition, board: boardState }, nextPlayerToMove);

    // Generate move notation
    const generateMoveNotation = (
        from: Position,
        to: Position,
        piece: Piece,
        capturedPiece: Piece | null,
        isCastling: boolean,
        isCheck: boolean,
        isCheckmate: boolean,
        isPromotion: boolean = false,
        promotionPiece?: PieceType,
        isEnPassant: boolean = false
    ): string => generateMoveNotationEngine(from, to, piece, capturedPiece, isCastling, isCheck, isCheckmate, isPromotion, promotionPiece, isEnPassant);

    const canControlCurrentTurn = (): boolean => {
        if (turnStartMs === null && gameHistory.length === 0) {
            return canControlTurnBeforeTimerStart(currentPlayer, playerControlColor);
        }
        return playerControlColor === null || currentPlayer === playerControlColor;
    };

    const finalizeDrawStateAfterMove = (movingColor: Color): void => {
        if (offerDrawOnNextMove) {
            setDrawOfferedBy(movingColor);
            setDrawAgreed(false);
            setOfferDrawOnNextMove(false);
            return;
        }

        if (drawOfferedBy) {
            setDrawOfferedBy(null);
        }
        setDrawAgreed(false);
    };

    // Shared function to execute move after drop (both mouse and touch)
    const executeDropMove = (fromRow: number, fromCol: number, toRow: number, toCol: number, piece: Piece): void => {
        // Prevent moves if game has ended due to timeout
        if (timeoutWinner) {
            return;
        }
        
        if ('vibrate' in navigator && isTouchDevice) {
            navigator.vibrate(100);
        }

        const capturedPiece = board[toRow][toCol];
        const isCastling = getCastlingSideFromMove(piece, fromRow, fromCol, toRow, toCol) !== null;

        const isEnPassantCapture = piece.piece === 'P' &&
            enPassantTarget &&
            toRow === enPassantTarget[0] &&
            toCol === enPassantTarget[1] &&
            Math.abs(fromCol - toCol) === 1 &&
            !board[toRow][toCol];

        const newBoard = executeMove(fromRow, fromCol, toRow, toCol, piece);

        if (piece.piece === 'P' &&
            ((piece.color === 'white' && toRow === 0) ||
                (piece.color === 'black' && toRow === 7))) {
            setPromotionSquare([toRow, toCol]);
            setGamePhase('promotion');
            setBoard(newBoard);
            return;
        }

        setBoard(newBoard);

        // Update timers for the move
        if (turnStartMs) {
            const timeSpentMs = Date.now() - turnStartMs;
            const incrementMs = applyIncrement(piece.color, timeSpentMs);

            if (piece.color === 'white') {
                setWhiteTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
            } else {
                setBlackTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
            }
        }

        const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
        setCurrentPlayer(nextPlayer);
        setLastMoveTimestamp(Date.now());
        setTurnStartMs(Date.now()); // Start opponent's timer

        finalizeDrawStateAfterMove(piece.color);
        const { isCheck, isCheckmate } = getCheckStatusAfterMove(newBoard, nextPlayer);

        const moveNotation = generateMoveNotation(
            [fromRow, fromCol],
            [toRow, toCol],
            piece,
            capturedPiece,
            isCastling,
            isCheck,
            isCheckmate,
            false,
            undefined,
            !!isEnPassantCapture
        );

        const newMove: GameMove = {
            from: [fromRow, fromCol],
            to: [toRow, toCol],
            piece: piece,
            capturedPiece: capturedPiece || undefined,
            notation: moveNotation,
            isCheck,
            isCheckmate,
            isCastling,
            isEnPassant: !!isEnPassantCapture
        };

        setGameHistory([...gameHistory, newMove]);
    };

    // Event Handlers
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, row: number, col: number): void => {
        if (gamePhase !== 'playing') {
            e.preventDefault();
            return;
        }

        const gameStatus = getGameStatus();
        if (gameStatus.status !== 'ongoing') {
            e.preventDefault();
            return;
        }

        if (!canControlCurrentTurn()) {
            e.preventDefault();
            return;
        }

        const piece = board[row][col];
        if (!piece || piece.color !== currentPlayer) {
            e.preventDefault();
            return;
        }

        setDraggedPiece(piece);
        setDraggedFrom([row, col]);
        setSelectedSquare([row, col]);
        setValidMoves(getValidMoves(row, col, piece.piece, piece.color));
        setIsDragging(true);

        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', '');

        const sourceRect = e.currentTarget.getBoundingClientRect();
        const cursorOffsetX = Math.min(Math.max(e.clientX - sourceRect.left, 0), sourceRect.width);
        const cursorOffsetY = Math.min(Math.max(e.clientY - sourceRect.top, 0), sourceRect.height);

        const dragElement = e.currentTarget.cloneNode(true) as HTMLElement;
        dragElement.style.opacity = '0.9';
        dragElement.style.position = 'absolute';
        dragElement.style.left = '-1000px';
        dragElement.style.top = '-1000px';
        dragElement.style.pointerEvents = 'none';
        document.body.appendChild(dragElement);
        e.dataTransfer.setDragImage(dragElement, cursorOffsetX, cursorOffsetY);

        setTimeout(() => {
            if (document.body.contains(dragElement)) {
                document.body.removeChild(dragElement);
            }
        }, 0);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        if (draggedPiece && draggedFrom) {
            e.dataTransfer.dropEffect = 'move';
        } else {
            e.dataTransfer.dropEffect = 'none';
        }
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setTimeout(() => {
            if (isDragging) {
                setDraggedPiece(null);
                setDraggedFrom(null);
                setIsDragging(false);
            }
        }, 0);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, row: number, col: number): void => {
        e.preventDefault();

        if (!draggedPiece || !draggedFrom) return;

        const [fromRow, fromCol] = draggedFrom;
        const [actualToRow, actualToCol] = convertKingOnRookToCastling(fromRow, fromCol, row, col, draggedPiece);
        const isValidMove = validMoves.some(([moveRow, moveCol]) => moveRow === actualToRow && moveCol === actualToCol);

        if (isValidMove) {
            executeDropMove(fromRow, fromCol, actualToRow, actualToCol, draggedPiece);
        }

        setDraggedPiece(null);
        setDraggedFrom(null);
        setSelectedSquare(null);
        setValidMoves([]);
        setIsDragging(false);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>, row: number, col: number): void => {
        if (gamePhase !== 'playing') return;

        const gameStatus = getGameStatus();
        if (gameStatus.status !== 'ongoing') return;
        if (!canControlCurrentTurn()) return;

        e.preventDefault();

        const piece = board[row][col];
        if (!piece || piece.color !== currentPlayer) {
            if (selectedSquare) {
                const [selectedRow, selectedCol] = selectedSquare;
                const movingPiece = board[selectedRow][selectedCol];

                if (movingPiece) {
                    const [actualToRow, actualToCol] = convertKingOnRookToCastling(selectedRow, selectedCol, row, col, movingPiece);
                    const isValidMove = validMoves.some(([moveRow, moveCol]) => moveRow === actualToRow && moveCol === actualToCol);

                    if (isValidMove) {
                        executeDropMove(selectedRow, selectedCol, actualToRow, actualToCol, movingPiece);
                    }
                }

                setSelectedSquare(null);
                setValidMoves([]);
            }
            setDraggedPiece(null);
            setDraggedFrom(null);
            setTouchStartPos(null);
            setIsDragging(false);
            return;
        }

        const touch = e.touches[0];
        setTouchStartPos({ x: touch.clientX, y: touch.clientY });
        setDraggedPiece(piece);
        setDraggedFrom([row, col]);
        setSelectedSquare([row, col]);
        setValidMoves(getValidMoves(row, col, piece.piece, piece.color));
        setIsDragging(true);

        if ('vibrate' in navigator) {
            navigator.vibrate(50);
        }

        e.stopPropagation();
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
        if (!isDragging || !touchStartPos) return;

        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartPos.x);
        const deltaY = Math.abs(touch.clientY - touchStartPos.y);

        if (deltaX > 10 || deltaY > 10) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedPiece || !draggedFrom || !touchStartPos) {
            setIsDragging(false);
            setTouchStartPos(null);
            return;
        }

        const touch = e.changedTouches[0];
        const deltaX = Math.abs(touch.clientX - touchStartPos.x);
        const deltaY = Math.abs(touch.clientY - touchStartPos.y);

        if (deltaX < 10 && deltaY < 10) {
            setDraggedPiece(null);
            setDraggedFrom(null);
            setIsDragging(false);
            setTouchStartPos(null);
            return;
        }

        const element = document.elementFromPoint(touch.clientX, touch.clientY);

        if (element) {
            let squareElement: HTMLElement | null = element as HTMLElement;
            while (squareElement && !squareElement.classList.contains('chess-square')) {
                squareElement = squareElement.parentElement;
            }

            if (squareElement && squareElement.dataset.position) {
                const [row, col] = squareElement.dataset.position.split('-').map(Number);

                if (row >= 0 && row < 8 && col >= 0 && col < 8) {
                    const [fromRow, fromCol] = draggedFrom;
                    const [actualToRow, actualToCol] = convertKingOnRookToCastling(fromRow, fromCol, row, col, draggedPiece);
                    const isValidMove = validMoves.some(([moveRow, moveCol]) => moveRow === actualToRow && moveCol === actualToCol);

                    if (isValidMove) {
                        executeDropMove(fromRow, fromCol, actualToRow, actualToCol, draggedPiece);
                        setSelectedSquare(null);
                        setValidMoves([]);
                    }
                }
            }
        }

        setDraggedPiece(null);
        setDraggedFrom(null);
        setIsDragging(false);
        setTouchStartPos(null);
    };

    const handleSquareClick = (row: number, col: number): void => {
        if (gamePhase !== 'playing') return;

        const gameStatus = getGameStatus();
        if (gameStatus.status !== 'ongoing') return;
        if (!canControlCurrentTurn()) return;

        const clickedPiece = board[row][col];

        if (selectedSquare) {
            const [selectedRow, selectedCol] = selectedSquare;
            const movingPiece = board[selectedRow][selectedCol];

            if (!movingPiece) {
                setSelectedSquare(null);
                setValidMoves([]);
                return;
            }

            const [actualToRow, actualToCol] = convertKingOnRookToCastling(selectedRow, selectedCol, row, col, movingPiece);
            const isValidMove = validMoves.some(([moveRow, moveCol]) => moveRow === actualToRow && moveCol === actualToCol);

            if (isValidMove) {
                const capturedPiece = board[actualToRow][actualToCol];
                const isCastling = getCastlingSideFromMove(movingPiece, selectedRow, selectedCol, actualToRow, actualToCol) !== null;

                const isEnPassantCapture = movingPiece.piece === 'P' &&
                    enPassantTarget &&
                    actualToRow === enPassantTarget[0] &&
                    actualToCol === enPassantTarget[1] &&
                    Math.abs(selectedCol - actualToCol) === 1 &&
                    !board[actualToRow][actualToCol];

                const newBoard = executeMove(selectedRow, selectedCol, actualToRow, actualToCol, movingPiece);

                if (movingPiece && movingPiece.piece === 'P' &&
                    ((movingPiece.color === 'white' && actualToRow === 0) ||
                        (movingPiece.color === 'black' && actualToRow === 7))) {
                    setPromotionSquare([actualToRow, actualToCol]);
                    setGamePhase('promotion');
                    setBoard(newBoard);
                    setSelectedSquare(null);
                    setValidMoves([]);
                    return;
                }

                setBoard(newBoard);

                // Update timers for the move
                if (turnStartMs) {
                    const timeSpentMs = Date.now() - turnStartMs;
                    const incrementMs = applyIncrement(movingPiece.color, timeSpentMs);

                    if (movingPiece.color === 'white') {
                        setWhiteTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
                    } else {
                        setBlackTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
                    }
                }

                const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
                setCurrentPlayer(nextPlayer);
                setLastMoveTimestamp(Date.now());
                setTurnStartMs(Date.now()); // Start opponent's timer
                finalizeDrawStateAfterMove(movingPiece.color);
                const { isCheck, isCheckmate } = getCheckStatusAfterMove(newBoard, nextPlayer);

                if (movingPiece) {
                    const moveNotation = generateMoveNotation(
                        selectedSquare,
                        [actualToRow, actualToCol],
                        movingPiece,
                        capturedPiece,
                        isCastling,
                        isCheck,
                        isCheckmate,
                        false,
                        undefined,
                        !!isEnPassantCapture
                    );

                    const newMove: GameMove = {
                        from: selectedSquare,
                        to: [actualToRow, actualToCol],
                        piece: movingPiece,
                        capturedPiece: capturedPiece || undefined,
                        notation: moveNotation,
                        isCheck,
                        isCheckmate,
                        isCastling,
                        isEnPassant: !!isEnPassantCapture
                    };

                    setGameHistory([...gameHistory, newMove]);
                }
            }

            setSelectedSquare(null);
            setValidMoves([]);
        } else if (clickedPiece && clickedPiece.color === currentPlayer) {
            setSelectedSquare([row, col]);
            const moves = getValidMoves(row, col, clickedPiece.piece, clickedPiece.color);
            setValidMoves(moves);
        }
    };

    const handlePromotion = (newPiece: PieceType): void => {
        if (!promotionSquare) return;
        
        // Prevent promotion if game has ended due to timeout
        if (timeoutWinner) {
            setPromotionSquare(null);
            setGamePhase('playing');
            return;
        }

        const [row, col] = promotionSquare;
        const newBoard = cloneBoard(board);
        newBoard[row][col] = { piece: newPiece, color: currentPlayer };
        const movingColor = currentPlayer;

        setBoard(newBoard);
        setPromotionSquare(null);
        setGamePhase('playing');

        // Update timers for the promotion move
        if (turnStartMs) {
            const timeSpentMs = Date.now() - turnStartMs;
            const incrementMs = applyIncrement(movingColor, timeSpentMs);

            if (movingColor === 'white') {
                setWhiteTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
            } else {
                setBlackTimeMs(prev => Math.max(0, prev - timeSpentMs + incrementMs));
            }
        }

        const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
        setCurrentPlayer(nextPlayer);
        setLastMoveTimestamp(Date.now());
        setTurnStartMs(Date.now()); // Start opponent's timer
        finalizeDrawStateAfterMove(movingColor);
        const { isCheck, isCheckmate } = getCheckStatusAfterMove(newBoard, nextPlayer);

        if (gameHistory.length > 0) {
            const lastMove = gameHistory[gameHistory.length - 1];

            const updatedNotation = generateMoveNotation(
                lastMove.from,
                lastMove.to,
                lastMove.piece,
                lastMove.capturedPiece || null,
                lastMove.isCastling || false,
                isCheck,
                isCheckmate,
                true,
                newPiece,
                lastMove.isEnPassant || false
            );

            const updatedMove: GameMove = {
                ...lastMove,
                notation: updatedNotation,
                isCheck,
                isCheckmate
            };

            const updatedHistory = [...gameHistory];
            updatedHistory[updatedHistory.length - 1] = updatedMove;
            setGameHistory(updatedHistory);
        }
    };

    const startGame = (configTimeControl?: TimeControl | null, chess960PositionId?: number | null): void => {
        // Initialize time control — use explicit arg, then hook state, then default 5 min
        const tc = configTimeControl !== undefined ? configTimeControl : timeControl;
        
        // Default to 5 minutes if no time control is specified
        const finalTimeControl = tc !== null && tc !== undefined ? tc : { minutes: 5, increment: 0, incrementType: 'none' as const };

        setTimeControl(finalTimeControl);
        
        if (finalTimeControl !== null) {
            const initialTimeMs = finalTimeControl.minutes * 60 * 1000;
            setWhiteTimeMs(initialTimeMs);
            setBlackTimeMs(initialTimeMs);
            setTurnStartMs(null);
        } else {
            setWhiteTimeMs(0);
            setBlackTimeMs(0);
            setTurnStartMs(null);
        }
        setLastMoveTimestamp(null);
        setTimeoutDetected(false);
        setTimeoutWinner(null);

        if (gameVariation === 'chess' || gameVariation === 'chessx2' || gameVariation === 'chess960' || gameVariation === 'chess960x2') {
            setPlayerPieces({ white: 'Q', black: 'Q' });
            setGamePhase('playing');
            setTimeout(() => initializeBoard(gameVariation, playerPieces, chess960PositionId), 0);
            setResignedBy(null);
            setDrawOfferedBy(null);
            setDrawAgreed(false);
            setOfferDrawOnNextMove(false);
            return;
        }
        else if (!gameVariation.includes('32') && playerPieces.white && playerPieces.black) {
            setGamePhase('playing');
            setTimeout(() => initializeBoard(gameVariation, playerPieces, chess960PositionId), 0);
            setResignedBy(null);
            setDrawOfferedBy(null);
            setDrawAgreed(false);
            setOfferDrawOnNextMove(false);
            return;
        }

        const isValid = gameVariation.includes('32')
            ? (playerPieces.white && playerPieces.black)
            : (playerPieces.white && playerPieces.black);

        if (isValid) {
            setGamePhase('playing');
            setTimeout(() => initializeBoard(gameVariation, playerPieces, chess960PositionId), 0);
            setResignedBy(null);
            setDrawOfferedBy(null);
            setDrawAgreed(false);
            setOfferDrawOnNextMove(false);
        }
    };

    const setGameVariation = (variation: GameVariation): void => {
        setGameVariationState(variation);
        if (variation === 'chess' || variation === 'chess960') {
            setPromotionRule('limited');
        }
        if (variation === 'chess960blind') {
            // Blind Chess960 uses ONE shared major piece for both players.
            // Normalize any leftover independent choices (e.g. from x32) so
            // the setup screen, the invite, and the game always agree.
            setPlayerPieces((current) => {
                const shared = current.white ?? current.black ?? 'Q';
                if (current.white === shared && current.black === shared) return current;
                return { white: shared, black: shared };
            });
        }
    };

    const resetGame = (): void => {
        setGamePhase('setup');
        setGameVariationState(DEFAULT_GAME_VARIATION);
        setPlayerPieces(DEFAULT_PLAYER_PIECES);
        setPromotionRule(DEFAULT_PROMOTION_RULE);
        setBoard(Array(8).fill(null).map(() => Array(8).fill(null)));
        setCurrentPlayer('white');
        setSelectedSquare(null);
        setValidMoves([]);
        setGameHistory([]);
        setResignedBy(null);
        setDrawOfferedBy(null);
        setDrawAgreed(false);
        setOfferDrawOnNextMove(false);
        setPromotionSquare(null);
        setDraggedPiece(null);
        setDraggedFrom(null);
        setEnPassantTarget(null);
        setCastlingRights({
            whiteKingSide: true,
            whiteQueenSide: true,
            blackKingSide: true,
            blackQueenSide: true
        });
        setKingPositions({ white: null, black: null });
        setRookPositions({
            whiteKingSide: null,
            whiteQueenSide: null,
            blackKingSide: null,
            blackQueenSide: null
        });
        // Reset timer state
        const defaultTimeControl = { minutes: 5, increment: 0, incrementType: 'none' as const };
        setTimeControl(defaultTimeControl);
        setWhiteTimeMs(defaultTimeControl.minutes * 60 * 1000);
        setBlackTimeMs(defaultTimeControl.minutes * 60 * 1000);
        setLastMoveTimestamp(null);
        setTurnStartMs(null);
        setTimeoutDetected(false);
        setTimeoutWinner(null);

        // Reset online sync bookkeeping for the next room/game.
        lastSentSnapshotRef.current = '';
        snapshotVersionRef.current = 0;
        pendingResyncRef.current = false;
    };

    const resignGame = (color: Color): void => {
        if (gamePhase !== 'playing') return;
        setOfferDrawOnNextMove(false);
        setDrawOfferedBy(null);
        setDrawAgreed(false);
        setResignedBy(color);
    };

    const queueDrawOffer = (): void => {
        if (gamePhase !== 'playing') return;
        if (getGameStatus().status !== 'ongoing') return;
        if (!canControlCurrentTurn()) return;
        if (drawOfferedBy) return;
        setOfferDrawOnNextMove(true);
    };

    const cancelQueuedDrawOffer = (): void => {
        setOfferDrawOnNextMove(false);
    };

    const acceptDrawOffer = (): void => {
        if (gamePhase !== 'playing') return;
        if (getGameStatus().status !== 'ongoing') return;
        if (!canControlCurrentTurn()) return;
        if (!drawOfferedBy || drawOfferedBy === currentPlayer) return;

        setDrawAgreed(true);
        setDrawOfferedBy(null);
        setOfferDrawOnNextMove(false);
        setSelectedSquare(null);
        setValidMoves([]);
        setDraggedPiece(null);
        setDraggedFrom(null);
        setTouchStartPos(null);
        setIsDragging(false);
    };

    const getPromotionPieces = (): PieceType[] =>
        getPromotionPieceTypes(gameVariation, promotionRule, playerPieces, currentPlayer);

    const getOnlineSnapshot = useCallback((): OnlineGameSnapshot => {
        const snapshot: OnlineGameSnapshot = {
            version: snapshotVersionRef.current,
            gameVariation,
            playerPieces,
            promotionRule,
            gamePhase,
            board,
            currentPlayer,
            gameHistory,
            promotionSquare,
            enPassantTarget,
            castlingRights,
            kingPositions,
            rookPositions,
            resignedBy,
            drawOfferedBy,
            drawAgreed,
            timeoutWinner,
            // Send real-time remaining time for the current player so the opponent
            // receives an accurate clock value, not the stale stored state.
            whiteTimeMs: (() => {
                if (whiteTimeMs <= 0) return undefined;
                if (currentPlayer === 'white' && turnStartMs) {
                    return Math.max(0, whiteTimeMs - (Date.now() - turnStartMs));
                }
                return whiteTimeMs;
            })(),
            blackTimeMs: (() => {
                if (blackTimeMs <= 0) return undefined;
                if (currentPlayer === 'black' && turnStartMs) {
                    return Math.max(0, blackTimeMs - (Date.now() - turnStartMs));
                }
                return blackTimeMs;
            })(),
            // Only emit lastMoveTimestamp once the game has actually started.
            // An absent timestamp signals "waiting to start" to applyOnlineSnapshot.
            lastMoveTimestamp: (turnStartMs !== null || gameHistory.length > 0) ? Date.now() : undefined
        };

        return snapshot;
    }, [gameVariation, playerPieces, promotionRule, gamePhase, board, currentPlayer, gameHistory, promotionSquare, enPassantTarget, castlingRights, kingPositions, rookPositions, resignedBy, drawOfferedBy, drawAgreed, timeoutWinner, whiteTimeMs, blackTimeMs, turnStartMs]);

    const applyOnlineSnapshot = useCallback((snapshot: OnlineGameSnapshot): void => {
        const incomingVersion = snapshot.version ?? 0;
        // While still in the setup phase, any incoming snapshot is the
        // authoritative game start (e.g. the server-authored Blind Chess960
        // initial position). Accept it regardless of the local version counter,
        // which may be stale from earlier games in this session.
        if (gamePhaseRef.current !== 'setup' && incomingVersion < snapshotVersionRef.current) {
            return;
        }

        // Update the last-sent ref so the sync effect won't echo this back
        lastSentSnapshotRef.current = JSON.stringify(snapshot);
        // Sync version to the received snapshot's version
        snapshotVersionRef.current = incomingVersion;
        // Block the sync effect from re-sending stale state until React state has flushed
        pendingResyncRef.current = true;

        setGameVariationState(snapshot.gameVariation);
        setPlayerPieces(snapshot.playerPieces);
        setPromotionRule(snapshot.promotionRule);
        setGamePhase(snapshot.gamePhase);
        setBoard(snapshot.board);
        setCurrentPlayer(snapshot.currentPlayer);

        // Handle history merging for optimized snapshots
        // If the incoming snapshot has truncated history (length < current history),
        // we should try to preserve our local history if it matches the latest move.
        // If it's a fresh load (empty local history), we just accept what we get.
        setGameHistory(prevHistory => {
            const newHistory = snapshot.gameHistory;
            if (newHistory.length > 0 && prevHistory.length > 0 && newHistory.length < prevHistory.length) {
                // Check if the latest move matches
                const lastNewMove = newHistory[newHistory.length - 1];
                const lastLocalMove = prevHistory[prevHistory.length - 1];
                
                // Simple equality check on move coordinates
                if (lastNewMove.from[0] === lastLocalMove.from[0] && 
                    lastNewMove.from[1] === lastLocalMove.from[1] &&
                    lastNewMove.to[0] === lastLocalMove.to[0] &&
                    lastNewMove.to[1] === lastLocalMove.to[1]) {
                    return prevHistory;
                }
            }
            // If we are loading fresh or history diverged/reset, take the server state
            return newHistory;
        });

        setPromotionSquare(snapshot.promotionSquare);
        setEnPassantTarget(snapshot.enPassantTarget);
        setCastlingRights(snapshot.castlingRights);
        setKingPositions(snapshot.kingPositions);
        setRookPositions(snapshot.rookPositions);
        setResignedBy(snapshot.resignedBy ?? null);
        setDrawOfferedBy(snapshot.drawOfferedBy ?? null);
        setDrawAgreed(snapshot.drawAgreed ?? false);
        // Apply the opponent's timeout result and stop our own detection loop.
        setTimeoutWinner(snapshot.timeoutWinner ?? null);
        setTimeoutDetected(false);
        // Apply timer state from snapshot
        if (snapshot.whiteTimeMs !== undefined) {
            setWhiteTimeMs(snapshot.whiteTimeMs);
        } else if (timeControl) {
            setWhiteTimeMs(timeControl.minutes * 60 * 1000);
        }
        if (snapshot.blackTimeMs !== undefined) {
            setBlackTimeMs(snapshot.blackTimeMs);
        } else if (timeControl) {
            setBlackTimeMs(timeControl.minutes * 60 * 1000);
        }
        if (snapshot.lastMoveTimestamp !== undefined) {
            setLastMoveTimestamp(snapshot.lastMoveTimestamp);
        }
        // Reset turn start time accounting for time already elapsed since the snapshot was sent.
        // lastMoveTimestamp is the wall-clock time when the snapshot was created on the sender's side.
        // By back-dating turnStartMs we avoid giving the current player extra free time on each sync.
        if (snapshot.gamePhase === 'playing') {
            if (snapshot.gameHistory.length === 0 && !snapshot.lastMoveTimestamp) {
                setTurnStartMs(null);
            } else if (snapshot.lastMoveTimestamp) {
                const elapsed = Date.now() - snapshot.lastMoveTimestamp;
                // Back-date turnStartMs so the clock continues from where it was
                setTurnStartMs(Date.now() - elapsed);
            } else {
                setTurnStartMs(Date.now());
            }
        }
        setOfferDrawOnNextMove(false);
        setSelectedSquare(null);
        setValidMoves([]);
        setDraggedPiece(null);
        setDraggedFrom(null);
        setTouchStartPos(null);
        setIsDragging(false);
    }, [timeControl]);

    // Effects

    // Timer countdown effect
    useEffect(() => {
        if (!timeControl || gamePhase !== 'playing' || !turnStartMs || timeoutWinner) return;

        const interval = setInterval(() => {
            const now = Date.now();
            const elapsed = now - turnStartMs;
            const currentPlayerTime = currentPlayer === 'white' ? whiteTimeMs : blackTimeMs;

            if (currentPlayerTime - elapsed <= 0) {
                setTimeoutDetected(true);
                clearInterval(interval);
            }
        }, 200);

        return () => clearInterval(interval);
    }, [timeControl, gamePhase, turnStartMs, currentPlayer, whiteTimeMs, blackTimeMs, timeoutWinner]);

    // Timeout detection effect
    useEffect(() => {
        if (timeoutDetected && gamePhase === 'playing' && !timeoutWinner) {
            // Use setTimeout to avoid cascading renders while still preventing race conditions
            const timeoutId = setTimeout(() => {
                const winner: Color = currentPlayer === 'white' ? 'black' : 'white';
                setTimeoutWinner(winner);
                setTimeoutDetected(false);
            }, 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [timeoutDetected, gamePhase, currentPlayer, timeoutWinner]);

    useEffect(() => {
        const checkTouchDevice = () => {
            setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
        };

        checkTouchDevice();
        window.addEventListener('resize', checkTouchDevice);

        return () => {
            window.removeEventListener('resize', checkTouchDevice);
        };
    }, []);

    return {
        gameVariation,
        setGameVariation,
        gamePhase,
        playerPieces,
        setPlayerPieces,
        promotionRule,
        setPromotionRule,
        board,
        currentPlayer,
        playerControlColor,
        setPlayerControlColor,
        selectedSquare,
        validMoves,
        gameHistory,
        drawOfferedBy,
        drawAgreed,
        offerDrawOnNextMove,
        promotionSquare,
        draggedPiece,
        draggedFrom,
        isTouchDevice,
        isDragging,
        // Timer state
        whiteTimeMs,
        blackTimeMs,
        timeControl,
        setTimeControl,
        turnStartMs,
        timeoutWinner,
        formatTime,
        getCurrentPlayerTimeRemaining,
        forceTimerStart,
        getGameStatus,
        isInCheck,
        startGame,
        startOnlineGame,
        prepareBlindSetup,
        resetGame,
        resignGame,
        queueDrawOffer,
        cancelQueuedDrawOffer,
        acceptDrawOffer,
        handleSquareClick,
        handleDragStart,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDragEnd,
        handleDrop,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handlePromotion,
        getPromotionPieces,
        getOnlineSnapshot,
        applyOnlineSnapshot,
        lastSentSnapshotRef,
        snapshotVersionRef,
        pendingResyncRef
    };
};
